import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Container from '../../../components/ui/Container';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import TextReveal from '../../../components/ui/TextReveal';
import ScrollReveal from '../../../components/ui/ScrollReveal';
import SectionHeading from '../../../components/ui/SectionHeading';

const FlipCardsSection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="py-section" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <Container>
        <ScrollReveal>
          <SectionHeading
            title={data.title}
            subtitle={data.description}
          />
        </ScrollReveal>

        <div className="flex flex-wrap justify-center gap-6">
          {data.cards.map((card, i) => (
            <FlipCard key={i} card={card} index={i} totalCards={data.cards.length} />
          ))}
        </div>
      </Container>
    </section>
  );
};

const FlipCard = ({ card, index, totalCards }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const centerIndex = Math.floor(totalCards / 2);
  const distFromCenter = Math.abs(index - centerIndex);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: distFromCenter * 0.1, duration: 0.5 }}
      className="w-[320px] h-[420px] perspective-[1000px] cursor-pointer"
      onClick={() => setIsFlipped(!isFlipped)}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <motion.div
        className="relative w-full h-full"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden glass-strong shadow-card"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="absolute inset-0">
            <OptimizedImage
              src={card.image.url}
              alt={card.image.alt}
              className="w-full h-full"
              wrapperClassName="w-full h-full"
              objectFit="cover"
              aspectRatio="320/420"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-900/80 via-brand-900/30 to-transparent" />
          </div>
          <div className="relative z-10 flex flex-col items-center justify-end h-full p-6 pb-10">
            <TextReveal
              text={card.title}
              as="h3"
              mode="word"
              stagger={0.05}
              className="text-2xl font-heading font-bold text-white text-center"
            />
          </div>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-card"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="absolute inset-0 aurora-bg opacity-90" />
          <div className="relative z-10 flex flex-col items-center justify-center h-full p-6">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={isFlipped ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="text-base text-white/90 text-center mb-6 leading-relaxed"
            >
              {card.description}
            </motion.p>
            {card.ctaButton && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={isFlipped ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.35 }}
              >
                <Link
                  to={card.ctaButton.link}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {card.ctaButton.text}
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FlipCardsSection;
